import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  getCopilotSessions,
  createCopilotSession,
  getCopilotSession,
  patchCopilotSession,
  deleteCopilotSession,
  askCopilot,
  getErrorMessage,
  type CopilotSession,
  type CopilotSkillUploadResult,
} from '../../utils/api'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotifications } from '../../contexts/NotificationContext'
import { useForwarding } from '../../contexts/ForwardingContext'
import { CopilotSessionPanel } from './CopilotSessionPanel'
import { CopilotComposer } from './CopilotComposer'
import { CopilotMessageBubble, type DisplayMessage } from './CopilotMessageBubble'
import { UpdateSkillDialog } from './UpdateSkillDialog'

const PROMPT_SUGGESTIONS = [
  'How do I create a quote?',
  'Where are VAT rates?',
  'How do revisions work?',
]

const SparkleIcon: React.FC = () => (
  <svg className="w-6 h-6 text-brand-600 dark:text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
  </svg>
)

/**
 * AI Copilot — a grounded, Claude-style chat that lives inside the dashboard's
 * existing left menu (no competing sidebar). The conversation is the primary,
 * centered surface; chat history is a slim collapsible rail on the left.
 */
export const AiCopilotView: React.FC = () => {
  const { currentAgent } = useAuth()
  const { addNotification } = useNotifications()
  const navigate = useNavigate()
  const { pending, consume, returnTo } = useForwarding()

  // Forwarded-message prefill for the composer (text + a nonce that bumps each
  // time so re-forwarding identical text still re-applies).
  const [prefill, setPrefill] = useState<string | null>(null)
  const [prefillNonce, setPrefillNonce] = useState(0)
  // Dismissible "Forwarded from …" context chip.
  const [showReturnChip, setShowReturnChip] = useState(false)

  const isPrivileged = currentAgent?.role === 'admin' || currentAgent?.role === 'supervisor'

  const [sessions, setSessions] = useState<CopilotSession[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(true)
  const [activeId, setActiveId] = useState<number | string | null>(null)
  const [messages, setMessages] = useState<DisplayMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [streaming, setStreaming] = useState(false)
  const [skillDialogOpen, setSkillDialogOpen] = useState(false)

  const [panelCollapsed, setPanelCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('wq_copilot_panel_collapsed')
      if (stored !== null) return stored === 'true'
    } catch {
      /* ignore */
    }
    // Default-collapsed on narrow widths.
    return typeof window !== 'undefined' && window.innerWidth < 768
  })

  const togglePanel = useCallback(() => {
    setPanelCollapsed((v) => {
      const next = !v
      try {
        localStorage.setItem('wq_copilot_panel_collapsed', String(next))
      } catch {
        /* ignore */
      }
      return next
    })
  }, [])

  const scrollRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Auto-scroll to the bottom as messages grow / tokens stream.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages])

  const refreshSessions = useCallback(async () => {
    try {
      const list = await getCopilotSessions()
      setSessions(list)
      return list
    } catch (err) {
      addNotification(getErrorMessage(err, 'Could not load chats'), 'error')
      return [] as CopilotSession[]
    } finally {
      setSessionsLoading(false)
    }
  }, [addNotification])

  useEffect(() => {
    refreshSessions()
    return () => abortRef.current?.abort()
  }, [refreshSessions])

  const loadSession = useCallback(
    async (id: number | string) => {
      // Don't switch away mid-stream.
      if (streaming) return
      setActiveId(id)
      setMessagesLoading(true)
      try {
        const detail = await getCopilotSession(id)
        setMessages(
          detail.messages.map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            citations: m.citations,
            attachments: m.attachments || [],
          }))
        )
      } catch (err) {
        addNotification(getErrorMessage(err, 'Could not load this chat'), 'error')
        setMessages([])
      } finally {
        setMessagesLoading(false)
      }
    },
    [streaming, addNotification]
  )

  const startNewChat = useCallback(() => {
    if (streaming) return
    setActiveId(null)
    setMessages([])
  }, [streaming])

  // ── Consume a forwarded message (chat/ticket → Copilot) ──
  // On entry, claim any pending forward whose target is the Copilot. Capture the
  // chosen session id from `pending` BEFORE consuming (consume clears it), then
  // open that session (or start a new chat) and hand the text to the composer to
  // prefill + focus. We never auto-send. Runs once per mount; consume() only
  // returns text when a matching pending forward exists, so direct loads no-op.
  useEffect(() => {
    const chosenSessionId = pending?.target.kind === 'copilot' ? pending.target.id : undefined
    const text = consume((t) => t.kind === 'copilot')
    if (text === null) return

    if (chosenSessionId !== undefined && chosenSessionId !== 'new') {
      // Open the chosen existing session (keeps its history).
      loadSession(chosenSessionId)
    } else {
      // "New chat" → empty conversation; send() lazily creates the session.
      startNewChat()
    }

    setPrefill(text)
    setPrefillNonce((n) => n + 1)
    if (returnTo) setShowReturnChip(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Send a question. Lazily creates a session on the first message of a new
   * chat, appends an optimistic user bubble + an empty streaming assistant
   * bubble, then drives the SSE stream into that bubble. After the first send
   * we refresh the session list so the server's auto-title appears.
   */
  const send = useCallback(
    async (text: string, attachments?: Array<{ url: string; type?: string; name?: string }>) => {
      if (streaming) return
      const atts = (attachments || []).map((a) => ({ url: a.url, type: a.type, name: a.name }))

      let sessionId = activeId

      if (sessionId === null) {
        try {
          const created = await createCopilotSession()
          sessionId = created.id
          setActiveId(created.id)
        } catch (err) {
          addNotification(getErrorMessage(err, 'Could not start a chat'), 'error')
          return
        }
      }

      const userMsg: DisplayMessage = {
        id: `local-user-${Date.now()}`,
        role: 'user',
        content: text,
        citations: null,
        attachments: atts,
      }
      const assistantId = `local-assistant-${Date.now()}`
      const assistantMsg: DisplayMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        citations: null,
        streaming: true,
      }
      setMessages((prev) => [...prev, userMsg, assistantMsg])
      setStreaming(true)

      const controller = new AbortController()
      abortRef.current = controller

      const updateAssistant = (patch: Partial<DisplayMessage>) =>
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, ...patch } : m))
        )

      try {
        await askCopilot(sessionId!, text, {
          signal: controller.signal,
          onDelta: (delta) =>
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, content: m.content + delta } : m
              )
            ),
          onDone: (_messageId, citations, fallback) => {
            // Fallback answers are ungrounded → show no sources.
            updateAssistant({
              streaming: false,
              citations: fallback ? [] : citations,
            })
          },
          onError: (code, message) => {
            const friendly =
              code === 'AI_NOT_CONFIGURED'
                ? "The AI Copilot isn't configured yet. Ask an administrator to set it up."
                : message
            // Replace any partial content cleanly with the error notice.
            updateAssistant({
              streaming: false,
              content: `⚠️ ${friendly}`,
              citations: [],
            })
            addNotification(friendly, 'error')
          },
        }, atts)
      } catch (err) {
        if (!controller.signal.aborted) {
          const msg = getErrorMessage(err, 'The Copilot request failed')
          updateAssistant({ streaming: false, content: `⚠️ ${msg}`, citations: [] })
          addNotification(msg, 'error')
        }
      } finally {
        setStreaming(false)
        abortRef.current = null
        // Refresh the list so a freshly auto-titled session (first message) or
        // an updated last_message_at re-orders/relabels correctly.
        refreshSessions()
      }
    },
    [streaming, activeId, addNotification, refreshSessions]
  )

  const handleRename = useCallback(
    async (id: number | string, title: string) => {
      // Optimistic.
      setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)))
      try {
        await patchCopilotSession(id, title)
      } catch (err) {
        addNotification(getErrorMessage(err, 'Could not rename chat'), 'error')
        refreshSessions()
      }
    },
    [addNotification, refreshSessions]
  )

  const handleDelete = useCallback(
    async (id: number | string) => {
      if (!window.confirm('Delete this chat? This cannot be undone.')) return
      try {
        await deleteCopilotSession(id)
        setSessions((prev) => prev.filter((s) => s.id !== id))
        if (id === activeId) {
          setActiveId(null)
          setMessages([])
        }
      } catch (err) {
        addNotification(getErrorMessage(err, 'Could not delete chat'), 'error')
      }
    },
    [activeId, addNotification]
  )

  const onSkillUploaded = useCallback(
    (result: CopilotSkillUploadResult) => {
      addNotification(
        `Updated to v${result.version} · ${result.changed} pages changed, ${result.carried_over} carried over`,
        'success'
      )
    },
    [addNotification]
  )

  const showEmptyState = !messagesLoading && messages.length === 0

  return (
    <div className="h-full flex overflow-hidden">
      <CopilotSessionPanel
        sessions={sessions}
        activeId={activeId}
        loading={sessionsLoading}
        collapsed={panelCollapsed}
        onToggleCollapse={togglePanel}
        onSelect={loadSession}
        onNewChat={startNewChat}
        onRename={handleRename}
        onDelete={handleDelete}
      />

      {/* Conversation column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-6 py-3 border-b border-border bg-surface">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-base font-semibold text-txt-primary truncate">AI Copilot</h1>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300 border border-brand-200 dark:border-brand-800">
              BETA
            </span>
          </div>
          {isPrivileged && (
            <button
              type="button"
              onClick={() => setSkillDialogOpen(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-txt-secondary hover:text-brand-600 hover:bg-surface-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              title="Update the Copilot's knowledge base"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
              Update skill
            </button>
          )}
        </div>

        {/* Scrollable conversation */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-6">
            {messagesLoading ? (
              <div className="flex justify-center py-20">
                <span className="inline-block w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin-slow" />
              </div>
            ) : showEmptyState ? (
              <div className="flex flex-col items-center text-center pt-16">
                <div className="mb-4 w-12 h-12 rounded-full bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center">
                  <SparkleIcon />
                </div>
                <h2 className="text-lg font-semibold text-txt-primary">How can I help?</h2>
                <p className="text-sm text-txt-secondary mt-1 max-w-md">
                  Ask about WeQuote and I&apos;ll answer from the knowledge base,
                  citing the pages I used.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {PROMPT_SUGGESTIONS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => send(p)}
                      disabled={streaming}
                      className="px-3 py-2 rounded-xl text-sm text-txt-primary bg-surface-secondary hover:bg-surface-hover border border-border transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {messages.map((m) => (
                  <CopilotMessageBubble key={m.id} message={m} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Composer */}
        <div className="shrink-0 border-t border-border bg-surface">
          <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-3">
            {showReturnChip && returnTo && (
              <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-brand-200 dark:border-brand-800 bg-brand-50 dark:bg-brand-900/20 px-3 py-1.5">
                <span className="inline-flex items-center gap-1.5 text-xs text-brand-700 dark:text-brand-300 min-w-0">
                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8.688c0-.864.933-1.405 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.953l-7.108 4.062A1.125 1.125 0 013 16.81V8.688zM12.75 8.688c0-.864.933-1.405 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.953l-7.108 4.062a1.125 1.125 0 01-1.683-.977V8.688z" />
                  </svg>
                  <span className="truncate">
                    Forwarded from {returnTo.kind === 'ticket' ? 'ticket' : 'chat'} with {returnTo.label}
                  </span>
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => navigate(returnTo.path)}
                    className="px-2 py-0.5 rounded-md text-xs font-medium text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-800/40 transition-colors"
                  >
                    Back to {returnTo.kind === 'ticket' ? 'ticket' : 'chat'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowReturnChip(false)}
                    aria-label="Dismiss"
                    className="p-0.5 rounded text-brand-500 hover:text-brand-700 dark:hover:text-brand-200 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            <CopilotComposer
              onSend={send}
              disabled={streaming}
              prefill={prefill}
              prefillNonce={prefillNonce}
            />
            <p className="mt-1.5 text-center text-[11px] text-txt-tertiary">
              Enter to send · Shift+Enter for a new line
            </p>
          </div>
        </div>
      </div>

      {skillDialogOpen && (
        <UpdateSkillDialog
          onClose={() => setSkillDialogOpen(false)}
          onUploaded={onSkillUploaded}
        />
      )}
    </div>
  )
}
